import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import {
  useChangeStatusMutation,
  useCountryCodeQuery,
  useCreateCustomerMutation,
  useCustomersQuery,
  useDeleteCustomerMutation,
  useSingleCustomerQuery,
  useUpdateCustomerMutation,
} from "../../Store/Apis/customerApi";
import Error from "../../Components/Error/Error";
import Loader from "../../Components/Loader/Loader";
import { Table } from "antd";
import { Modal } from "react-bootstrap";
import AddEditCustomerForm from "../../Components/AddEditCustomerForm/AddEditCustomerForm";
import CompanyControl from "../../Components/CompanyControl/CompanyControl";
import toast from "react-hot-toast";
import { isPermissionGranted } from "../../Hooks/permissionsMiddleware";
import { useSelector } from "react-redux";
import { useOrderByCustomerIdQuery } from "../../Store/Apis/bookingApi";

const Customers = () => {
  // Bulk upload states
  const [bulkModalShow, setBulkModalShow] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  // Bulk upload handler
  const [bulkRows, setBulkRows] = useState([]);
  const handleBulkFileChange = async (e) => {
    const file = e.target.files[0];
    setBulkFile(file);
    let rows = [];
    if (file) {
      try {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const allRows = XLSX.utils.sheet_to_json(worksheet);
        // Only keep rows with at least one required field
        rows = allRows.filter(
          (row) => row.name || row.email || row.phone || row.outletID
        );
        if (allRows.length > 0 && rows.length === 0) {
          toast.error(
            "No valid records found. Please fill in at least the required fields (name, email, phone, outletID) in your template."
          );
        }
      } catch (err) {
        toast.error("Failed to read file. Please check your format.");
      }
    }
    setBulkRows(rows);
  };

  const handleBulkUpload = async () => {
    if (!bulkFile) {
      toast.error("Please select a file first.");
      return;
    }
    if (bulkRows.length === 0) {
      toast.error(
        "No valid records found in your file. Please ensure required fields are filled."
      );
      return;
    }
    setBulkLoading(true);
    let successCount = 0;
    let failCount = 0;
    for (const row of bulkRows) {
      const body = {
        name: row.name || row.customerName || "",
        email: row.email || "",
        countryCode: row.countryCode?.toString() || "",
        phone: row.phoneNumber?.toString() || row.phone?.toString() || "",
        taxNumber: row.taxNumber?.toString() || "",
        address: row.address || "",
        isActive: row.isActive ? 1 : 0,
        outletID: row.outletID || "",
        clientType: row.clientType || "individual",
      };
      try {
        const res = await addCustomer(body);
        if (res?.data?.status === 200) {
          successCount++;
        } else {
          failCount++;
        }
      } catch {
        failCount++;
      }
    }
    toast.success(
      `Bulk upload complete! Success: ${successCount}, Failed: ${failCount}`
    );
    setBulkModalShow(false);
    setBulkFile(null);
    setBulkRows([]);
    setBulkLoading(false);
  };
  const user = useSelector((state) => state.auth.user);
  const [activeTab, setActiveTab] = useState("customers"); // "customers" or "companies"
  const [sortFilter, setSortFilter] = useState({
    search: "",
    outletID: "",
  });

  const customerApi = useCustomersQuery({
    keyword: sortFilter.search,
    outletID: sortFilter.outletID,
  });

  const [orderModalShow, setOrderModalShow] = useState(null);
  const oderByCustomerId = useOrderByCustomerIdQuery(orderModalShow, {
    skip: !orderModalShow,
  });

  const [addCustomer, addCustomerResponse] = useCreateCustomerMutation();
  const [addModalShow, setAddModalShow] = useState(false);
  const AddCustomer = () => {
    const handleFormSubmit = (formData) => {
      // Build minimal body; removed fields per new requirements
      const body = {
        name: formData.customerName,
        email: formData.email,
        countryCode: formData.countryCode.toString(),
        phone: formData.phoneNumber.toString(),
        taxNumber: formData.taxNumber?.toString() || "",
        address: formData.address,
        isActive: formData.isActive ? 1 : 0,
        outletID: formData.outletID,
        clientType:
          formData.clientType === "individual" ? "individual" : "Company",
      };

      addCustomer(body)
        .then((res) => {
          if (res?.data?.status === 200) {
            setAddModalShow(false);
            toast.success(res?.data?.message, { duration: 7000 });
          } else {
            toast.error(res?.data?.message, { duration: 7000 });
          }
        })
        .catch(() => {
          toast.error("An error occurred. Please try again.", {
            duration: 7000,
          });
        });
    };
    return (
      <>
        <Modal
          show={addModalShow}
          onHide={() => setAddModalShow(false)}
          size="md"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Add New Client</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AddEditCustomerForm handleSubmit={handleFormSubmit} isNew={true} />
          </Modal.Body>
        </Modal>
      </>
    );
  };

  // UPDATE CUSTOMER
  const [updateCustomerId, setUpdateCustomerId] = useState(null);
  const [updateCustomerModalShow, setupdateCustomerModalShow] = useState(false);
  const singleCustomer = useSingleCustomerQuery(
    {
      customerID: updateCustomerId,
    },
    { skip: !updateCustomerId }
  );
  const [updateCustomer, updateCustomerResponse] = useUpdateCustomerMutation();

  const UpdateCustomer = () => {
    const handleFormSubmit = (formData) => {
      // Add or edit customer logic here
      console.log("Form data received from child:", formData);

      const body = {
        id: updateCustomerId,
        salutation: formData.salutation || "N/A",
        name: formData.customerName,
        // Preserve provided date, do not auto-fill current date
        dateOfBirth: formData.dateOfBirth || "",
        email: formData.email,
        countryCode: formData.countryCode.toString(),
        customerRating: formData.customerRating
          ? parseInt(formData.customerRating)
          : 0,
        phone: formData.phoneNumber.toString(),
        taxNumber: formData.taxNumber || "N/A",
        locality: formData.locality || "N/A",
        pin: formData.pincode || "0000",
        address: formData.address,
        // Do not send isActive from edit form; status changes happen via dedicated action
        outletID: formData.outletID,
        // Respect client type (customer vs company)
        Type: formData.clientType === "company" ? "Company" : "Customer",
      };
      console.log(body);

      updateCustomer(body)
        .then((res) => {
          console.log(res);
          if (res?.data?.status === 200) {
            setupdateCustomerModalShow(false);
            toast.success(res?.data?.message, { duration: 7000 });
            // Ensure the list reflects latest status from server
            try {
              customerApi?.refetch && customerApi.refetch();
            } catch (e) {
              console.warn("customerApi.refetch failed", e);
            }
          } else {
            toast.error(res?.data?.message, { duration: 7000 });
          }
        })
        .catch((err) => {
          toast.error("An error occurred. Please try again.", {
            duration: 7000,
          });
        });
    };

    return (
      <>
        <Modal
          show={updateCustomerModalShow}
          onHide={() => setupdateCustomerModalShow(false)}
          size="md"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>View and Edit Customer</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <AddEditCustomerForm
              handleSubmit={handleFormSubmit}
              customerData={
                singleCustomer.isSuccess && {
                  salutation: singleCustomer.data.data[0].salutation,
                  customerName: singleCustomer.data.data[0].customerName,
                  dateOfBirth: singleCustomer.data.data[0].dateOfBirth,
                  countryCode: singleCustomer.data.data[0].countryCode,
                  phoneNumber: singleCustomer.data.data[0].phoneNumber,
                  email: singleCustomer.data.data[0].email,
                  taxNumber: singleCustomer.data.data[0].taxNumber,
                  customerRating: singleCustomer.data.data[0].customerRating,
                  locality: singleCustomer.data.data[0].locality,
                  pincode: singleCustomer.data.data[0].pin,
                  address: singleCustomer.data.data[0].address,
                  isActive: singleCustomer.data.data[0].isActive,
                }
              }
            />
          </Modal.Body>
        </Modal>
      </>
    );
  };

  // STAUS CHANGE
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [changeStatus] = useChangeStatusMutation();
  const handleChangeStatus = () => {
    // Hard guard: never change status while edit modal is open
    if (updateCustomerModalShow) {
      setSelectedStatus(null);
      return;
    }
    changeStatus({
      keyID: selectedStatus.keyID,
      isActive: selectedStatus.isActive,
      Type: "Individual",
    }).then((res) => {
      console.log(res);
      setSelectedStatus(null);
    });
  };

  // DELETE
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [deleteCustomer] = useDeleteCustomerMutation();
  const handleDelete = () => {
    deleteCustomer({
      keyID: selectedRecord,
      Type: "Individual",
    }).then((res) => {
      console.log(res);
      setSelectedRecord(null);
    });
  };

  useEffect(() => {
    if (user.role !== "superAdmin") {
      setSortFilter({
        ...sortFilter,
        outletID: user.outletID,
      });
    }
  }, []);

  const columns = [
    {
      title: "Customer Details",
      render: (i, action) => (
        <>
          <p className="mb-1 text-sm">{action.customerName}</p>
          <p className="mb-1 text-sm">{action.phoneNumber}</p>
        </>
      ),
    },
    {
      title: "Customer Address",
      dataIndex: "address",
      key: "address",
    },
    {
      title: "Order History",
      render: (i, action) => (
        <>
          {/* <p className='mb-1 text-sm'>Total Order Amount - R {action.orderAmount}</p> */}
          <button
            type="button"
            className={`btn badge badge-primary badge-xs fw-600 text-xs mb-0`}
            onClick={() => setOrderModalShow(action.customerID)}
          >
            View Orders
          </button>
        </>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (i, action) => (
        <button
          type="button"
          className={`btn badge badge-xs ${
            Number(action.isActive) === 1 ? "badge-success" : "badge-danger"
          } fw-600 text-xs mb-0`}
          disabled={updateCustomerModalShow}
          onClick={() =>
            setSelectedStatus({
              keyID: action.customerID,
              isActive: Number(action.isActive) === 1 ? 0 : 1,
            })
          }
        >
          {Number(action.isActive) === 1 ? "Active" : "Deactivated"}
        </button>
      ),
    },
    {
      title: "Actions",
      dataIndex: "actions",
      key: "actions",
      render: (i, action) =>
        isPermissionGranted(20) && (
          <>
            <button
              type="button"
              className="btn badge badge-xs badge-warning fw-600 text-xs mb-0"
              onClick={() => (
                setSelectedStatus(null),
                setUpdateCustomerId(action.customerID),
                setupdateCustomerModalShow(true)
              )}
            >
              View & Edit
            </button>

            <button
              type="button"
              className="btn ms-2 badge badge-xs badge-danger text-xs fw-600 mb-0"
              onClick={() => setSelectedRecord(action.customerID)}
              disabled={action.hasOrder === 1}
            >
              Delete
            </button>
          </>
        ),
    },
  ];

  return (
    <>
      {/* Bulk Upload Modal */}
      <Modal
        show={bulkModalShow}
        onHide={() => setBulkModalShow(false)}
        size="md"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Bulk Upload Customers/Companies</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <a
              href="/documents/BulkUploadTemplate.xlsx"
              download
              className="btn btn-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Download Bulk Upload Template
            </a>
          </div>
          <div className="mb-3">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleBulkFileChange}
              disabled={bulkLoading}
            />
          </div>
          <div className="mb-3">
            <button
              className="btn btn-primary"
              onClick={handleBulkUpload}
              disabled={bulkLoading || !bulkFile}
            >
              {bulkLoading
                ? "Adding..."
                : bulkFile
                ? bulkRows.length > 0
                  ? `Add ${bulkRows.length} Record(s)`
                  : "Add (No valid records found)"
                : "Add"}
            </button>
          </div>
        </Modal.Body>
      </Modal>
      <div className="row align-items-center justify-content-between mb-4">
        <div className="col">
          <h5 className="fw-500 text-white">Client Management</h5>
        </div>
        <div className="col-auto d-flex align-items-center gap-2">
          {isPermissionGranted(20) && (
            <>
              <a
                button="button"
                className="btn btn-icon btn-3 btn-white text-primary mb-1 mx-1"
                onClick={() => {
                  if (activeTab === "customers") {
                    setAddModalShow(true);
                  } else {
                    // For companies tab, we'll need to trigger the CompanyControl modal
                    // This will be handled by passing a prop to CompanyControl
                    // For now, we'll show an alert to indicate the functionality
                    const event = new CustomEvent("openCompanyModal");
                    window.dispatchEvent(event);
                  }
                }}
              >
                <i className="fa fa-plus me-2" /> Add New{" "}
                {activeTab === "customers" ? "Customer" : "Company"}
              </a>
              <button
                className="btn btn-outline-primary mb-1 mx-1"
                onClick={() => setBulkModalShow(true)}
              >
                Bulk Upload
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-white border-bottom">
              <ul className="nav nav-tabs border-0" role="tablist">
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${
                      activeTab === "customers" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("customers")}
                    type="button"
                    role="tab"
                    style={{
                      border: "none",
                      borderBottom:
                        activeTab === "customers"
                          ? "2px solid #0d6efd"
                          : "none",
                      color: activeTab === "customers" ? "#0d6efd" : "#6c757d",
                      fontWeight: activeTab === "customers" ? "600" : "500",
                    }}
                  >
                    <i className="fa fa-user me-2"></i>
                    Customers
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link ${
                      activeTab === "companies" ? "active" : ""
                    }`}
                    onClick={() => setActiveTab("companies")}
                    type="button"
                    role="tab"
                    style={{
                      border: "none",
                      borderBottom:
                        activeTab === "companies"
                          ? "2px solid #0d6efd"
                          : "none",
                      color: activeTab === "companies" ? "#0d6efd" : "#6c757d",
                      fontWeight: activeTab === "companies" ? "600" : "500",
                    }}
                  >
                    <i className="fa fa-building me-2"></i>
                    Companies
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === "customers" ? (
        /* Customer View */
        <div className="row">
          <div className="col-12">
            <div className="card mb-4">
              <div className="card-header p-4">
                <div className="row">
                  <div className="col-md-12 mb-1">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by Name, Email and Phone"
                      value={sortFilter?.search || ""}
                      onChange={(e) =>
                        setSortFilter({ ...sortFilter, search: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="card-body p-0">
                {customerApi.isError && <Error />}
                {customerApi.isLoading && <Loader height="500px" />}
                {customerApi.isSuccess && (
                  <>
                    <Table
                      columns={columns}
                      dataSource={customerApi?.data?.data?.filter(
                        (item) =>
                          item.clientType === "Customer" ||
                          item.clientType === "individual" ||
                          !item.clientType // fallback for items without clientType
                      )}
                    />
                  </>
                )}
                <AddCustomer />
                <UpdateCustomer />
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Company View */
        <CompanyControl />
      )}

      {/* STATUS CHANGE MODAL */}
      <Modal
        show={selectedStatus}
        onHide={() => setSelectedStatus(null)}
        size="sm"
        centered
      >
        <Modal.Body className="text-center p-4">
          <h5>Are you sure you want to change status?</h5>
          <div className="buttons d-flex gap-3 justify-content-center">
            <button
              className="btn btn-danger bg-danger btn-sm mb-0"
              onClick={() => setSelectedStatus(null)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm mb-0"
              onClick={handleChangeStatus}
            >
              Confirm
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* DELETE MODAL */}
      <Modal
        show={selectedRecord}
        onHide={() => setSelectedRecord(null)}
        size="sm"
        centered
      >
        <Modal.Body className="text-center p-4">
          <h5>Are you sure you want to delete the customer?</h5>
          <div className="buttons d-flex gap-3 justify-content-center">
            <button
              className="btn btn-danger bg-danger btn-sm mb-0"
              onClick={() => setSelectedRecord(null)}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm mb-0"
              onClick={handleDelete}
            >
              Confirm
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* VIEW ORDERS MODAL */}
      <Modal
        show={orderModalShow}
        onHide={() => setOrderModalShow(null)}
        size="md"
        centered
      >
        <Modal.Header>
          <Modal.Title>Order Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {oderByCustomerId.isLoading || oderByCustomerId.isFetching ? (
            <Loader height="300px" />
          ) : oderByCustomerId.isError ? (
            <Loader height="300px" />
          ) : (
            <div>
              {oderByCustomerId.isSuccess &&
                (oderByCustomerId.data.data.length > 0 ? (
                  oderByCustomerId.data.data.map((elem, i) => (
                    <div key={i}>
                      <p className="fw-bold text-sm text-dark mb-0">
                        Order Id - {elem.orderId}
                      </p>
                      <p className="text-xs text-dark mb-1">
                        Date - {elem.orderDate.split("T")[0]}
                      </p>
                      <p className="text-xs text-dark mb-2">
                        Total - R{" "}
                        {elem.serviceTypeAndName.reduce((acc, it) => {
                          return acc + it.rate;
                        }, 0)}
                      </p>
                      <div className="row mb-3 g-2">
                        {elem.serviceTypeAndName.map((service, index) => (
                          <div className="col-lg-4 col-md-4 col-3" key={index}>
                            <div className="border-dashed border-1 border-secondary border-radius-md position-relative p-2 h-100 d-block mb-0 m-0">
                              <span className="text-sm font-weight-bold mb-1 d-block text-dark">
                                {service.productServiceName}
                              </span>
                              <span className="text-xs font-weight-bold mb-1 d-block">
                                {service.serviceName}
                              </span>
                              <span
                                className="d-block text-danger"
                                style={{ fontSize: "12px", marginTop: "0px" }}
                              >
                                Rate: {service.rate}
                              </span>
                              <span
                                className="d-block text-success"
                                style={{ fontSize: "12px", marginTop: "0px" }}
                              >
                                Quantity: {service.quantity}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-sm text-center my-5">
                    No Data found !!
                  </div>
                ))}
            </div>
          )}
          <div className="buttons d-flex gap-3 justify-content-center">
            <button
              className="btn btn-danger bg-danger btn-sm mb-0"
              onClick={() => setOrderModalShow(null)}
            >
              Close
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Customers;
